import { resolvePunctuationLineBreak } from '@src/editor/services/captionLineBreakStyle';

/**
 * adoptTranslatedCaptionRows.ts
 *
 * Second half of translated-track reconcile — the COUNTERPART to
 * `reconcileTranslatedCaptionRows`.
 *
 * That module answers "which existing translated rows survive, and which of
 * their words came back?" — it only ever KEEPS or SHRINKS rows, never creates
 * one. But a transcript restore can bring back words whose phrase survives in NO
 * row (its row was deleted whole, or every word of it was removed then
 * restored). Those words have a translation — the phrase that was built for them
 * — but nothing on the track to show it. Left to the original-language token
 * walk they surface as a SOURCE-language caption row inside a translated track
 * (and drag every unowned punctuation mark into junk rows). This module gives
 * them their translated rows instead.
 *
 * The rule, mirroring the confirmed spec:
 *   - New rows are segmented BY PHRASE (via the producer's own
 *     `resolvePhraseCaptionRows` + `mergeResolvedPhrasesIntoSegments`), so their
 *     text, punctuation folding and line lengths match a fresh produce.
 *   - A phrase already owned by a surviving row is NEVER emitted again — the
 *     boundary word it shares was already absorbed into that row by
 *     `reconcileTranslatedCaptionRows`. This is what stops a phrase spanning the
 *     seam between an existing clip and a new one from duplicating.
 *   - Adjacency is respected: a run of new rows is only merged into one caption
 *     line while it is CONTIGUOUS in transcript order. An owned (surviving) row
 *     between two unowned phrases breaks the run, so a new line never spans an
 *     existing one (which would overlap it on the timeline).
 *   - A restored word that no active phrase covers yields NO row (it has no
 *     translation to show). Per the agreed scope this state should not arise in
 *     practice; emitting a source-language row would be the worse failure.
 *
 * Pure — no editor, no MobX, no IO. `KaraokeTrackManager` composes the result
 * with the surviving rows.
 */
import { KaraokeClip } from '../models/karaoke/KaraokeClip';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { IdGenerator } from './IdGenerator';
import {
  makePhraseForTokenResolver,
  makeTranslatedValueResolver,
  MAX_SEGMENT_CHAR_LENGTH,
  mergeResolvedPhrasesIntoSegments,
  resolveConfiguredMaxCharLength,
  type ResolvedPhraseRow,
  resolvePhraseCaptionRows,
} from './karaokeSegmentation';

import type { KaraokeConfig } from '../models/karaoke/KaraokeConfig';
import type { TranscriptToken } from '../types';
import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * Build the translated caption rows for phrases that no surviving row owns.
 *
 * @param survivingRows  Phrase-derived rows kept by `reconcileTranslatedCaptionRows`.
 * @param activeTokens   The full active transcript, in order.
 * @param phrases        Active translation phrases for the caption language.
 * @param config         Default karaoke config stamped onto the new clips.
 */
export function adoptTranslatedCaptionRows(
  survivingRows: readonly KaraokeClip[],
  activeTokens: readonly TranscriptToken[],
  phrases: readonly TranslationPhrase[],
  config: KaraokeConfig,
): KaraokeClip[] {
  if (phrases.length === 0) return [];

  const resolvePhrase = makePhraseForTokenResolver(phrases);
  const resolveTranslatedValue = makeTranslatedValueResolver(phrases);

  // A phrase is OWNED when a surviving row already carries one of its words —
  // that row is where its (possibly restored) boundary token belongs, so we must
  // not build a second row for it. Resolved through the same join the rows will
  // be, so identity matches.
  const ownedPhrases = new Set<TranslationPhrase>();
  for (const row of survivingRows) {
    for (const token of row.transcriptTokens) {
      const phrase = resolvePhrase(token);
      if (phrase) ownedPhrases.add(phrase);
    }
  }

  const resolved = resolvePhraseCaptionRows(
    [...activeTokens],
    phrases,
    resolveTranslatedValue,
    resolvePhrase,
  );

  const maxCharLength =
    resolveConfiguredMaxCharLength(config.transcript_maximum_length) ??
    MAX_SEGMENT_CHAR_LENGTH;

  // Walk the resolved rows in transcript order, accumulating a run of unowned
  // rows. An owned row breaks the run (it will render between the new lines, so
  // a new line must not span it). Each run is merged into caption segments on
  // its own, exactly as a fresh produce would.
  const clips: KaraokeClip[] = [];
  let run: ResolvedPhraseRow[] = [];
  const flush = () => {
    if (run.length === 0) return;
    for (const segment of mergeResolvedPhrasesIntoSegments(
      run,
      maxCharLength,
      resolvePunctuationLineBreak(config.line_break_style),
    )) {
      if (
        segment.tokens.length === 0 ||
        segment.startTime === null ||
        segment.endTime === null
      ) {
        continue;
      }
      clips.push(
        new KaraokeClip({
          id: IdGenerator.shortRandomId(),
          transcriptTokens: segment.tokens,
          hiddenTranscriptTokenIds: [],
          ignoredByFiltersIds: [],
          karaokeConfig: config,
          track: KARAOKE_TRACK_NUMBER,
          translatedText: segment.translatedText,
          translatedPhrases: segment.translatedPhrases,
          phraseOriginalIndices: segment.phraseOriginalIndices,
          startSecondsOffset: segment.startSecondsOffset,
          endSecondsOffset: segment.endSecondsOffset,
          siblingGroupId: segment.siblingGroupId,
        }),
      );
    }
    run = [];
  };

  for (const row of resolved) {
    if (ownedPhrases.has(row.phrase)) {
      flush();
      continue;
    }
    run.push(row);
  }
  flush();

  return clips;
}
